import React, { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { api, post } from "../api";

interface User {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  allowed_customers: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<User>("/api/auth/me")
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const data = await post<{ token: string; user: User }>("/api/auth/dev-login", { email, password });
    localStorage.setItem("vista_token", data.token);
    setUser(data.user);
  };

  const logout = async () => {
    await post("/api/auth/logout", {}).catch(() => {});
    localStorage.removeItem("vista_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
